import styled, { css } from 'styled-components'

import { Colors } from 'common-constants/colors'
import {
  Position,
  TextPosition,
} from 'components/presentational/Tooltip/Tooltip.types'
import { zIndex } from 'components/presentational/zIndex'

const marginVertical = 30
const arrowWidth = 20

const indentArrow = (indent: number) => {
  if (arrowWidth > indent) {
    return indent - Math.abs(arrowWidth - indent) / 2
  }

  return indent + Math.abs(arrowWidth - indent) / 2
}

interface TooltipWrapperProps {
  $width: number
  $position: Position
  $textPosition: TextPosition
  $withArrow: boolean
  $indent: number
}

const Arrow = css`
  content: '';
  display: block;
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
`

const ArrowTop = css`
  top: 100%;
  border-width: 20px 10px 0;
  border-color: ${Colors.white} transparent transparent transparent;
`

const ArrowBottom = css`
  bottom: 100%;
  border-width: 0 10px 20px;
  border-color: transparent transparent ${Colors.white} transparent;
`

const ArrowLeft = css`
  ${(props) => props.theme.left}: 100%;

  border-width: 10px 0 10px 20px;
  border-color: transparent transparent transparent ${Colors.white};
`

const ArrowRight = css`
  ${(props) => props.theme.right}: 100%;

  border-width: 10px 20px 10px 0;
  border-color: transparent ${Colors.white} transparent transparent;
`

const ArrowTopLeft = css<TooltipWrapperProps>`
  ${ArrowTop};
  ${(props) => props.theme.left}: ${(props) => indentArrow(props.$indent)}px;
`
const ArrowTopRight = css<TooltipWrapperProps>`
  ${ArrowTop};
  ${(props) => props.theme.right}: ${(props) => indentArrow(props.$indent)}px;
`

const ArrowBottomLeft = css<TooltipWrapperProps>`
  ${ArrowBottom}
  ${(props) => props.theme.left}: ${(props) => indentArrow(props.$indent)}px;
`
const ArrowBottomRight = css<TooltipWrapperProps>`
  ${ArrowBottom}
  ${(props) => props.theme.right}: ${(props) => indentArrow(props.$indent)}px;
`

const ArrowLeftTop = css<TooltipWrapperProps>`
  ${ArrowLeft};
  top: ${(props) => indentArrow(props.$indent)}px;
`
const ArrowLeftBottom = css<TooltipWrapperProps>`
  ${ArrowLeft};
  bottom: ${(props) => indentArrow(props.$indent)}px;
`

const ArrowRightTop = css<TooltipWrapperProps>`
  ${ArrowRight};
  top: ${(props) => indentArrow(props.$indent)}px;
`
const ArrowRightBottom = css<TooltipWrapperProps>`
  ${ArrowRight};
  bottom: ${(props) => indentArrow(props.$indent)}px;
`

export const TooltipWrapper = styled.div<TooltipWrapperProps>`
  display: grid;
  place-items: center;
  position: absolute;
  min-width: ${(props) => props.$width}px;
  padding: 15px;
  background-color: ${Colors.white};
  border-radius: 10px;
  filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.15));
  will-change: filter;
  text-align: ${(props) => props.$textPosition};
  font-size: 13px;

  &::after {
    ${Arrow};
    ${(props) => findPositionArrow(props.$position)}
  }
`

const findPositionArrow = (position: Position) => {
  switch (position) {
    case Position.TopLeft:
      return ArrowTopLeft
    case Position.TopCenter:
      return ArrowTop
    case Position.TopRight:
      return ArrowTopRight
    case Position.BottomLeft:
      return ArrowBottomLeft
    case Position.BottomCenter:
      return ArrowBottom
    case Position.BottomRight:
      return ArrowBottomRight
    case Position.LeftTop:
      return ArrowLeftTop
    case Position.LeftCenter:
      return ArrowLeft
    case Position.LeftBottom:
      return ArrowLeftBottom
    case Position.RightTop:
      return ArrowRightTop
    case Position.RightCenter:
      return ArrowRight
    case Position.RightBottom:
      return ArrowRightBottom
    default:
      return ArrowTopLeft
  }
}

const LeftTop = css<TooltipElementProps>`
  justify-content: flex-end;
  align-items: flex-start;
  transform: translateY(-${(props) => props.$indent / 2}px);

  ${TooltipWrapper} {
    ${(props) => props.theme.right}: ${marginVertical}px;

    top: -${(props) => props.$indent}px;
  }
`

const LeftCenter = css`
  justify-content: flex-end;
  align-items: center;

  ${TooltipWrapper} {
    ${(props) => props.theme.right}: ${marginVertical}px;
  }
`

const LeftBottom = css<TooltipElementProps>`
  justify-content: flex-end;
  align-items: flex-end;
  transform: translateY(${(props) => props.$indent / 2}px);

  ${TooltipWrapper} {
    ${(props) => props.theme.right}: ${marginVertical}px;

    bottom: -${(props) => props.$indent}px;
  }
`

const RightTop = css<TooltipElementProps>`
  justify-content: flex-start;
  align-items: flex-start;
  transform: translateY(-${(props) => props.$indent / 2}px);

  ${TooltipWrapper} {
    ${(props) => props.theme.left}: ${marginVertical}px;

    top: -${(props) => props.$indent}px;
  }
`

const RightCenter = css`
  justify-content: flex-start;
  align-items: center;

  ${TooltipWrapper} {
    ${(props) => props.theme.left}: ${marginVertical}px;
  }
`

const RightBottom = css<TooltipElementProps>`
  justify-content: flex-start;
  align-items: flex-end;
  transform: translateY(${(props) => props.$indent / 2}px);

  ${TooltipWrapper} {
    ${(props) => props.theme.left}: ${marginVertical}px;

    bottom: -${(props) => props.$indent}px;
  }
`

const TopLeft = css<TooltipElementProps>`
  justify-content: flex-start;
  align-items: flex-end;
  transform: translateX(
    -${(props) => (props.$indent * props.theme.multiplier) / 2}px
  );

  ${TooltipWrapper} {
    bottom: ${({ $indent }) => ($indent ? $indent : marginVertical)}px;
    ${(props) => props.theme.left}: -${(props) => props.$indent}px;
  }
`

const TopCenter = css<TooltipElementProps>`
  justify-content: center;
  align-items: flex-end;

  ${TooltipWrapper} {
    bottom: ${({ $indent }) => ($indent ? $indent : marginVertical)}px;
  }
`

const TopRight = css<TooltipElementProps>`
  justify-content: flex-end;
  align-items: flex-end;
  transform: translateX(
    ${(props) => (props.$indent * props.theme.multiplier) / 2}px
  );

  ${TooltipWrapper} {
    bottom: ${marginVertical}px;
    ${(props) => props.theme.right}: -${(props) => props.$indent}px;
  }
`

const BottomLeft = css<TooltipElementProps>`
  justify-content: flex-start;
  align-items: flex-start;
  transform: translateX(
    -${(props) => (props.$indent * props.theme.multiplier) / 2}px
  );

  ${TooltipWrapper} {
    top: ${marginVertical}px;
    ${(props) => props.theme.left}: -${(props) => props.$indent}px;
  }
`

const BottomCenter = css<TooltipElementProps>`
  justify-content: center;
  align-items: flex-start;

  ${TooltipWrapper} {
    top: ${({ $indent }) => ($indent ? $indent : marginVertical)}px;
  }
`

const BottomRight = css<TooltipElementProps>`
  justify-content: flex-end;
  align-items: flex-start;
  transform: translateX(
    ${(props) => (props.$indent * props.theme.multiplier) / 2}px
  );

  ${TooltipWrapper} {
    top: ${marginVertical}px;
    ${(props) => props.theme.right}: -${(props) => props.$indent}px;
  }
`

interface TooltipElementProps {
  $position: Position
  $indent: number
  $zIndex?: number
}

const findPositionTooltip = (position: Position) => {
  switch (position) {
    case Position.TopLeft:
      return TopLeft
    case Position.TopCenter:
      return TopCenter
    case Position.TopRight:
      return TopRight
    case Position.BottomLeft:
      return BottomLeft
    case Position.BottomCenter:
      return BottomCenter
    case Position.BottomRight:
      return BottomRight
    case Position.LeftTop:
      return LeftTop
    case Position.LeftCenter:
      return LeftCenter
    case Position.LeftBottom:
      return LeftBottom
    case Position.RightTop:
      return RightTop
    case Position.RightCenter:
      return RightCenter
    case Position.RightBottom:
      return RightBottom
    default:
      return BottomLeft
  }
}

export const TooltipElement = styled.div<TooltipElementProps>`
  display: flex;
  position: relative;
  height: 0;
  width: 0;
  ${(props) => findPositionTooltip(props.$position)}
  z-index: ${(props) => props.$zIndex ?? zIndex.navigationHeader};
  color: ${Colors.dark};
`

const ChangeOrderCss = css`
  ${TooltipElement} {
    order: -1;
  }
`

const FlexRowCss = css`
  flex-direction: row;
`

const FlexRowWithChangeOrderCss = css`
  ${FlexRowCss}
  ${ChangeOrderCss}
`

const FlexColumnCss = css`
  flex-direction: column;
`

const FlexColumnWithChangeOrderCss = css`
  ${FlexColumnCss}
  ${ChangeOrderCss}
`

const findWrapperPosition = (position: Position) => {
  switch (position) {
    case Position.TopLeft:
    case Position.TopCenter:
    case Position.TopRight:
      return FlexColumnWithChangeOrderCss

    case Position.BottomLeft:
    case Position.BottomCenter:
    case Position.BottomRight:
      return FlexColumnCss

    case Position.LeftTop:
    case Position.LeftCenter:
    case Position.LeftBottom:
      return FlexRowWithChangeOrderCss

    case Position.RightTop:
    case Position.RightCenter:
    case Position.RightBottom:
      return FlexRowCss

    default:
      return FlexColumnCss
  }
}

interface WrapperPosition {
  $position: Position
  $textPosition?: TextPosition
}

export const WrapperPosition = styled.div<WrapperPosition>`
  display: inline-flex;
  align-items: center;
  ${(props) => findWrapperPosition(props.$position)}
  vertical-align: top;
`

export const TooltipText = styled.p`
  margin: 0 0 10px;
`
